// src/features/audittrail/RetentionFormDialog.tsx
// Create/edit a retention policy — how long records of a given entity-type
// pattern are kept, and what happens on expiry.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { audittrailStore } from '@core/stores';
import { Button, Input, Textarea, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import type { ComplianceFramework, PurgeStrategy, RetentionPolicy } from '@core/types/audittrail';
import { AudittrailModal } from './Modal';
import { COMPLIANCE_FRAMEWORK_VALUES, PURGE_STRATEGY_VALUES, enumKey, errMessage } from './logic';

interface RetentionFormDialogProps {
  open: boolean;
  policy: RetentionPolicy | null;
  onClose: () => void;
  onSaved: () => void;
}

export const RetentionFormDialog = observer(({ open, policy, onClose, onSaved }: RetentionFormDialogProps) => {
  const { t } = useI18n();
  const [pattern, setPattern] = useState('');
  const [description, setDescription] = useState('');
  const [days, setDays] = useState(365);
  const [framework, setFramework] = useState<ComplianceFramework>('COMPLIANCE_FRAMEWORK_LOCAL');
  const [strategy, setStrategy] = useState<PurgeStrategy>('PURGE_STRATEGY_ARCHIVE');
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isEdit = !!policy?.entity?.id;

  useEffect(() => {
    if (!open) return;
    setPattern(policy?.entity_type_pattern ?? '');
    setDescription(policy?.description ?? '');
    setDays(policy?.retention_days ?? 365);
    setFramework(policy?.compliance_framework ?? 'COMPLIANCE_FRAMEWORK_LOCAL');
    setStrategy(policy?.purge_strategy ?? 'PURGE_STRATEGY_ARCHIVE');
    setActive(policy?.is_active ?? true);
    setError(null);
  }, [open, policy]);

  const submit = async () => {
    if (!pattern.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (isEdit && policy) {
        await audittrailStore.UpdateRetentionPolicy(policy.entity!.id, {
          policy: { ...policy, entity_type_pattern: pattern.trim(), description, retention_days: days, compliance_framework: framework, purge_strategy: strategy, is_active: active },
        });
      } else {
        await audittrailStore.CreateRetentionPolicy({
          entity_type_pattern: pattern.trim(),
          description,
          retention_days: days,
          compliance_framework: framework,
          purge_strategy: strategy,
          is_active: active,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('audittrail.retention.form.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AudittrailModal
      open={open}
      title={isEdit ? t('audittrail.retention.form.editTitle') : t('audittrail.retention.form.createTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={submit} isLoading={saving} disabled={!pattern.trim()}>{t('audittrail.retention.form.submit')}</Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <ErrorAlert error={error} />}
        <Input label={t('audittrail.retention.form.pattern')} helperText={t('audittrail.retention.form.patternHelp')} value={pattern} onChange={(e) => setPattern(e.target.value)} required />
        <Textarea label={t('audittrail.retention.form.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            min={1}
            label={t('audittrail.retention.form.days')}
            value={days}
            onChange={(e) => setDays(e.target.value === '' ? 0 : Number(e.target.value))}
          />
          <Select
            label={t('audittrail.retention.form.framework')}
            value={framework}
            onChange={(e) => setFramework(e.target.value as ComplianceFramework)}
            options={COMPLIANCE_FRAMEWORK_VALUES.map((f) => ({ value: f, label: t(`audittrail.complianceFramework.${enumKey(f, 'COMPLIANCE_FRAMEWORK_')}`) }))}
          />
        </div>
        <Select
          label={t('audittrail.retention.form.strategy')}
          value={strategy}
          onChange={(e) => setStrategy(e.target.value as PurgeStrategy)}
          options={PURGE_STRATEGY_VALUES.map((s) => ({ value: s, label: t(`audittrail.purgeStrategy.${enumKey(s, 'PURGE_STRATEGY_')}`) }))}
        />
        <ToggleSwitch label={t('audittrail.retention.form.active')} checked={active} onChange={setActive} />
      </div>
    </AudittrailModal>
  );
});

export default RetentionFormDialog;
